import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { ExecutionResultAdapter, ExecutionResultDto } from 'src/app/_models/execution-result-model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EmailMessageReportsRetrievingModel } from 'src/app/_models/messaging/messages/message-reports/email-reports/email-message-reports-retrieving-model';
import { DetailedEmailReportRequest } from 'src/app/_models/messaging/messages/message-reports/detailed-reports/detailed-email-report-request';
import { SMSMessageReportsRetrievingModel } from 'src/app/_models/messaging/messages/message-reports/sms-reports/sms-message-reports-retrieving-model';
import { SMSRepliesRetrievingModel } from 'src/app/_models/messaging/messages/message-reports/sms-reports/sms-replies-retrieving-model';
import { MessageTypeEnum } from 'src/app/_models/messaging/messages/message-type-enum';
import { HelperService } from '../../system/helpers/helper.service';
import {
  MessageReportGridViewModel
} from '../../../_models/messaging/messages/message-reports/email-reports/message-report-grid-view-model';

@Injectable({
  providedIn: 'root'
})
export class MessageReportsService {
  private baseUrl = environment.apiURL;
  private emailControllerName = 'EmailMessageReports';
  private smsControllerName = 'SMSMessageReports';
  private rcsControllerName = 'RCSMessageReports';

  constructor(
    private http: HttpClient,
    private executionResultAdapter: ExecutionResultAdapter,
    private helperService: HelperService
  ) { }

  getFolders(): Observable<ExecutionResultDto> {
    return this.http
    .get(`${this.baseUrl}${this.emailControllerName}/GetFolders`)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getEmailReports(reportModel: EmailMessageReportsRetrievingModel): Observable<ExecutionResultDto> {
    return this.http
    .post(`${this.baseUrl}${this.emailControllerName}/GetReports`, reportModel)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getEmailReportDetails(reportID: number): Observable<ExecutionResultDto> {
    const query = `reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.emailControllerName}/GetReportDetails?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getDetailedReportData(getDetailedReportRequest: DetailedEmailReportRequest, messageTypeEnum: MessageTypeEnum): Observable<ExecutionResultDto> {
    let controllerName: string;
    
    if (messageTypeEnum === MessageTypeEnum.email) {
      controllerName = this.emailControllerName;
    } else if (messageTypeEnum === MessageTypeEnum.sms) {
      controllerName = this.smsControllerName;
    } else if (messageTypeEnum === MessageTypeEnum['rcs-text'] || 
               messageTypeEnum === MessageTypeEnum['rcs-rich-card'] || 
               messageTypeEnum === MessageTypeEnum['rcs-carousel']) {
      controllerName = this.rcsControllerName;
    } else {
      controllerName = MessageTypeEnum[messageTypeEnum] + 'MessageReports';
    }
    
    return this.http
    .post(`${this.baseUrl}${controllerName}/GetDetailedReportData`, getDetailedReportRequest)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getClickedLinks(reportID: number): Observable<ExecutionResultDto> {
    const query = `reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.emailControllerName}/GetClickedLinks?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getSMSReports(reportModel: SMSMessageReportsRetrievingModel): Observable<ExecutionResultDto> {
    return this.http
    .post(`${this.baseUrl}${this.smsControllerName}/GetReports`, reportModel)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getSMSReportDetails(reportID: number): Observable<ExecutionResultDto> {
    const query = `reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.smsControllerName}/GetReportDetails?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getSMSReplies(repliesModel: SMSRepliesRetrievingModel): Observable<ExecutionResultDto> {
    return this.http
    .post(`${this.baseUrl}${this.smsControllerName}/GetSMSReplies`, repliesModel)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getSMSDetailedReportData(filter: string, reportID: number): Observable<ExecutionResultDto> {
    const query = `filter=${filter}&reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.smsControllerName}/GetDetailedReportData?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getRCSReports(reportModel: SMSMessageReportsRetrievingModel): Observable<ExecutionResultDto> {
    return this.http
    .post(`${this.baseUrl}${this.rcsControllerName}/GetReports`, reportModel)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getRCSReportDetails(reportID: number): Observable<ExecutionResultDto> {
    const query = `reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.rcsControllerName}/GetReportDetails?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getRCSReplies(repliesModel: SMSRepliesRetrievingModel): Observable<ExecutionResultDto> {
    return this.http
    .post(`${this.baseUrl}${this.rcsControllerName}/GetRCSReplies`, repliesModel)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  getRCSDetailedReportData(filter: string, reportID: number): Observable<ExecutionResultDto> {
    const query = `filter=${filter}&reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.rcsControllerName}/GetDetailedReportData?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

    getRCSSuggestionStats(reportID: number): Observable<ExecutionResultDto> {
      const query = `reportID=${reportID}`;
      return this.http
      .get(`${this.baseUrl}${this.rcsControllerName}/GetSuggestionStats?` + query)
      .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
    }

    getRCSSuggestionTypeStats(reportID: number): Observable<ExecutionResultDto> {
      const query = `reportID=${reportID}`;
      return this.http
      .get(`${this.baseUrl}${this.rcsControllerName}/GetSuggestionTypeStats?` + query)
      .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
    }

    getRCSInteractionDetails(reportID: number, pagerModel: any): Observable<ExecutionResultDto> {
      const model = { reportID, pagerModel };
      return this.http
      .post(`${this.baseUrl}${this.rcsControllerName}/GetInteractionDetails`, model)
      .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
    }

  getABTestReportDetails(reportID: number): Observable<ExecutionResultDto> {
    const query = `reportID=${reportID}`;
    return this.http
    .get(`${this.baseUrl}${this.emailControllerName}/GetABTestReportDetailsAsync?` + query)
    .pipe(map((data: any) => this.executionResultAdapter.adapt(data)));
  }

  export(data: MessageReportGridViewModel[], columnsForExport: any[], isSms = false, isRcs = false): void {
    columnsForExport.pop();
    const rows = data.map((row) => {
      const exportRows = {};
      columnsForExport.forEach((col) => {
        exportRows[col.displayName] = row[col.name];
      });

      return exportRows;
    });

    const reportName = isRcs ? 'RCSReport' : (isSms ? 'SMSReport' : 'EmailReport');
    const reportFullName = isRcs ? 'RCS Report' : (isSms ? 'SMS Report' : 'Email Report');
    const today = new Date();
    const fileName = `${reportName}_${ today.getDate() }${ today.getMonth() + 1 }${ today.getFullYear() }`;
    this.helperService.exportToCSV(fileName, rows);
    this.helperService.sentEmailWarningNotificationOfFileDownload(reportFullName, today);
  }
}
